import { useCallback, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';

import ElementIcon from '../ElementIcon';
import FormattedMessage from './FormattedMessage';
import { Icon } from 'src/components/ui/Icon';

// Language-neutral progress keys emitted by the backend stream, mapped to
// translatable English strings (es.json carries the Spanish).
const STATUS_LABELS = {
  analyzing: 'Analyzing your question…',
  querying: 'Querying data…',
  drafting: 'Writing the response…',
};

function AIChatModal({ agent, agentTyping, agentError, agentStatus, chatContext, messages, onSend, onClose, onClear, windowMode, onPopOut, onDock }) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);

  const handleSend = useCallback(() => {
    const val = input.trim();
    if (!val) return;
    setInput('');
    onSend(val);
  }, [input, onSend]);

  const onKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Autoscroll to the newest content (new messages OR streaming tokens).
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, agentTyping, agentStatus]);

  const statusText = agentStatus
    ? t(STATUS_LABELS[agentStatus] || agentStatus)
    : t('{{name}} is typing', { name: agent.name });

  const iconBtn =
    'inline-flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/15 hover:text-white';

  const panelClass = windowMode
    ? 'relative h-full w-full' // fills the detached window
    : 'pointer-events-auto absolute bottom-5 right-5 h-[560px] max-h-[85vh] w-[400px] max-w-[92vw] rounded-2xl border theme-border shadow-2xl ring-1 ring-black/5';

  const panel = (
    <div className={`flex flex-col overflow-hidden theme-bg-primary ${panelClass}`}>
      {/* Header */}
        <div className="ai-chat-drag flex shrink-0 cursor-grab items-center gap-3 px-4 py-3 text-white active:cursor-grabbing theme-accent-bg">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-lg">
            <ElementIcon element={agent.icon} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold leading-tight">{agent.name}</div>
            <div className="flex items-center gap-1.5 text-[11px] text-white/70">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
              {t('Online')}
            </div>
          </div>
          {messages?.length > 0 ? (
            <button type="button" title={t('Clear conversation')} onClick={onClear} className={iconBtn}>
              <Icon name="trash" />
            </button>
          ) : null}
          {windowMode ? (
            <button type="button" title={t('Dock back')} onClick={onDock} className={iconBtn}>
              <Icon name="compress" />
            </button>
          ) : (
            <button type="button" title={t('Open in a new window')} onClick={onPopOut} className={iconBtn}>
              <Icon name="external-link" />
            </button>
          )}
          <button type="button" title={t('Close')} onClick={onClose} className={iconBtn}>
            <Icon name="times" />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="ai-chat-scroll flex-1 space-y-1 overflow-y-auto py-3 theme-bg-primary">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl text-2xl theme-accent-bg-soft theme-accent-text">
                <ElementIcon element={agent.icon} />
              </span>
              <div className="text-sm font-semibold theme-text-primary">{agent.name}</div>
              <p className="text-xs theme-text-muted">
                {t('Ask me about your cases: counts, analytics, a specific case, workload…')}
              </p>
            </div>
          ) : (
            messages.map((message, index) => (
              <FormattedMessage key={index} message={message} chatContext={chatContext} />
            ))
          )}

          {agentTyping ? (
            <div className="flex items-center gap-2 px-4 py-1.5 text-xs theme-text-muted">
              <span className="flex gap-1">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full theme-accent-bg" style={{ animationDelay: '0ms' }} />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full theme-accent-bg" style={{ animationDelay: '150ms' }} />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full theme-accent-bg" style={{ animationDelay: '300ms' }} />
              </span>
              <span className="italic">{statusText}</span>
            </div>
          ) : agentError ? (
            <div className="px-4 py-1.5 text-xs text-red-600 dark:text-red-400">{String(agentError)}</div>
          ) : null}
        </div>

        {/* Input */}
        <div className="flex shrink-0 items-end gap-2 border-t theme-border theme-bg-primary p-2.5">
          <textarea
            rows={1}
            value={input}
            disabled={agentTyping}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('Type your message…')}
            className="ai-chat-input max-h-28 min-h-[40px] flex-1 resize-none rounded-xl border theme-border theme-bg-secondary px-3 py-2 text-[13px] theme-text-primary outline-none transition focus:ring-2 focus:ring-[var(--accent-color)] disabled:opacity-60"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={agentTyping || !input.trim()}
            title={t('Send')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white transition theme-accent-bg hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Icon name="paper-plane-o" />
          </button>
        </div>
    </div>
  );

  // In a detached window it fills the window (no drag). Docked, it's a draggable
  // corner panel.
  if (windowMode) return panel;
  return (
    <Draggable bounds="parent" handle=".ai-chat-drag">
      {panel}
    </Draggable>
  );
}

export default AIChatModal;
